import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }) : "-");
const idr = (v) => "Rp" + Number(v || 0).toLocaleString("id-ID");

export default function Invoice() {
  const { invId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = user && (user.role === "OWNER" || user.role === "ADMIN_INVENTORY");
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get(`/inventory/invoices/${invId}`).then(({ data }) => setDoc(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [invId]);
  useEffect(() => { load(); }, [load]);

  const printInvoice = () => {
    api.post(`/inventory/invoices/${invId}/mark-printed`).catch(() => {});
    window.print();
  };

  if (user && !canView) {
    return <div><AppTopBar active="sales" /><main className="page"><p className="hint">Akses ditolak.</p></main></div>;
  }

  return (
    <div>
      <AppTopBar active="sales" />
      <main className="page" style={{ maxWidth: 900, margin: "0 auto" }}>
        <div className="page-head no-print">
          <button className="btn-ghost" onClick={() => navigate("/inventory/manual-sales")} data-testid="invoice-back-button">← Kembali</button>
          {doc && <button className="btn-primary" onClick={printInvoice} data-testid="invoice-print-button">Cetak Invoice (A4)</button>}
        </div>
        {error && <div className="alert-error no-print" data-testid="invoice-error">{error}</div>}

        {doc && (
          <section className="panel print-area" data-testid="invoice-print-area">
            <div className="do-header">
              <div>
                <div className="do-title">INVOICE</div>
                <div className="do-subtitle">Faktur Penjualan</div>
              </div>
              <div className="do-meta">
                <div><strong>No:</strong> <span data-testid="invoice-number">{doc.invoice_number}</span></div>
                <div><strong>Tanggal:</strong> {fmtDate(doc.invoice_date)}</div>
                <div><strong>Ref Penjualan:</strong> {doc.sale_number}</div>
                {doc.reference && <div><strong>Referensi:</strong> {doc.reference}</div>}
              </div>
            </div>

            <div className="do-parties">
              <div className="do-party">
                <div className="do-party-label">PENJUAL</div>
                <div className="do-party-name">{doc.business_name || "-"}</div>
                {doc.business_address && <div>{doc.business_address}</div>}
              </div>
              <div className="do-party">
                <div className="do-party-label">KEPADA</div>
                <div className="do-party-name" data-testid="invoice-customer">{doc.customer_name || "UMUM"}</div>
                {doc.customer_phone && <div>Telp/WA: {doc.customer_phone}</div>}
                {doc.billing_address && <div data-testid="invoice-billing">{doc.billing_address}</div>}
              </div>
            </div>

            <table className="do-table" data-testid="invoice-items-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>No</th>
                  <th>SKU</th>
                  <th>Deskripsi</th>
                  <th style={{ width: 60 }}>Qty</th>
                  <th style={{ width: 80 }}>Satuan</th>
                  <th style={{ width: 120 }}>Harga</th>
                  <th style={{ width: 130 }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {doc.items.map((it, i) => (
                  <tr key={i} data-testid={`invoice-item-${it.sku_code}`}>
                    <td>{i + 1}</td>
                    <td className="mono">{it.sku_code}</td>
                    <td>{it.description}</td>
                    <td className="mono">{it.uom_qty}</td>
                    <td className="mono">{it.uom_code}</td>
                    <td className="mono">{idr(it.unit_selling_price)}</td>
                    <td className="mono">{idr(it.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} style={{ textAlign: "right", fontWeight: 700 }}>TOTAL</td>
                  <td className="mono" style={{ fontWeight: 700 }} data-testid="invoice-total">{idr(doc.total)}</td>
                </tr>
              </tfoot>
            </table>

            {doc.note && <div className="do-note"><strong>Catatan:</strong> {doc.note}</div>}

            <div className="do-signatures">
              {["Hormat Kami", "Penerima"].map((label) => (
                <div className="do-sign" key={label}>
                  <div className="do-sign-label">{label}</div>
                  <div className="do-sign-space" />
                  <div className="do-sign-line">Nama: __________________</div>
                  <div className="do-sign-line">Tanggal: ________________</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
